/**
 * StimulusFlow — Stimulus-based focus group UI
 * Setup form, running view with sticky stimulus reference, and report.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Play, Loader2, X, ChevronDown, ChevronUp, Users, FileText, Save, FolderOpen, Trash2
} from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import { useModels } from '../hooks/ModelsContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';


// ─── Setup Stage ────────────────────────────────────────────

export function StimulusSetup({ stimulus, config, personas, updateConfig, generatePersonas, isGeneratingPersonas, library }) {
  const { t } = useTranslation();
  const { models: MODELS, resolveModel } = useModels();
  const [showModeratorSettings, setShowModeratorSettings] = useState(false);
  const [panelSource, setPanelSource] = useState('generate'); // 'generate' | 'library'
  const [selectedPanelId, setSelectedPanelId] = useState('');
  const [panelModelAssignments, setPanelModelAssignments] = useState({});
  const modelIds = Object.keys(MODELS);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/configs?config_type=stimulus`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.configs || []);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const saveTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      const templateConfig = {
        topic: config.topic || '',
        stimulus_text: stimulus.stimulusText,
        stimulus_label: stimulus.stimulusLabel,
        questions: stimulus.questions.filter(q => q.trim()),
        moderatorBriefing: config.moderatorBriefing || '',
        moderatorDirection: config.moderatorDirection || '',
        moderatorAvoid: config.moderatorAvoid || '',
        moderatorModel: config.moderatorModel || 'claude-sonnet-4.6',
        devilsAdvocate: config.devilsAdvocate || false,
      };
      await fetch(`${API_BASE}/configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: templateName,
          config: templateConfig,
          selected_models: [],
          config_type: 'stimulus',
        }),
      });
      setShowSaveModal(false);
      setTemplateName('');
      fetchTemplates();
    } catch (err) {
      console.error('Failed to save template:', err);
    } finally {
      setSavingTemplate(false);
    }
  };

  const loadSelectedTemplate = (template) => {
    const c = template.config || {};
    if (c.topic) updateConfig('topic', c.topic);
    if (c.moderatorBriefing) updateConfig('moderatorBriefing', c.moderatorBriefing);
    if (c.moderatorDirection) updateConfig('moderatorDirection', c.moderatorDirection);
    if (c.moderatorAvoid) updateConfig('moderatorAvoid', c.moderatorAvoid);
    if (c.moderatorModel) updateConfig('moderatorModel', c.moderatorModel);
    if (c.devilsAdvocate !== undefined) updateConfig('devilsAdvocate', c.devilsAdvocate);
    stimulus.loadTemplate(template);
  };

  const deleteTemplate = async (templateId) => {
    try {
      await fetch(`${API_BASE}/configs/${templateId}`, { method: 'DELETE' });
      fetchTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const getPanelModel = (personaId, index) => {
    if (panelModelAssignments[personaId]) return panelModelAssignments[personaId];
    const persona = library?.panelPersonas?.find(p => p.id === personaId);
    if (persona?.default_model) {
      const resolved = resolveModel(persona.default_model);
      if (MODELS[resolved]) return resolved;
    }
    return modelIds[index % modelIds.length];
  };

  const hasLibraryPersonas = panelSource === 'library' && selectedPanelId && library?.panelPersonas?.length > 0;

  const canStart = config.topic
    && stimulus.stimulusText.trim()
    && stimulus.questions.filter(q => q.trim()).length > 0
    && (personas.length > 0 || hasLibraryPersonas);

  const validQuestionCount = stimulus.questions.filter(q => q.trim()).length;

  const handleStart = () => {
    if (panelSource === 'library' && hasLibraryPersonas) {
      // Map library personas to session format with user-assigned model
      // Generate new unique IDs per session to avoid DB primary key conflicts
      const mappedPersonas = library.panelPersonas.map((p, idx) => {
        const modelKey = getPanelModel(p.id, idx);
        const modelInfo = MODELS[modelKey] || {};
        return {
          id: crypto.randomUUID().slice(0, 8),
          library_persona_id: p.id,
          name: p.name,
          age: p.age || 35,
          gender: p.gender || '',
          region: p.region || '',
          employment: p.employment || '',
          education: p.education || '',
          family_status: p.family_status || '',
          personality_traits: p.personality_traits || p.traits || [],
          background_story: p.background_story || p.background || '',
          model_id: modelKey,
          provider: modelInfo.provider || '',
          values: p.values || [],
          political_leaning: p.political_leaning || '',
          worldview: p.worldview || '',
          communication_style: p.communication_style || '',
          argumentation_style: p.argumentation_style || '',
          openness_to_change: p.openness_to_change ?? 5,
        };
      });
      stimulus.startStimulus(config, mappedPersonas);
    } else {
      stimulus.startStimulus(config, personas);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="font-serif text-3xl mb-2">{t('stimulus.title')}</h1>
        <p className="text-stone-500">{t('stimulus.subtitle')}</p>
      </div>

      {/* Topic */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">{t('stimulus.topicLabel')}</label>
          <input
            type="text"
            value={config.topic || ''}
            onChange={e => updateConfig('topic', e.target.value)}
            className="input w-full"
            placeholder={t('stimulus.topicPlaceholder')}
          />
        </div>
      </section>

      {/* Stimulus Box */}
      <section className="bg-white rounded-2xl shadow-sm border-l-4 border-l-amber-400 border border-stone-200 p-8">
        <div className="flex items-center gap-2 mb-6">
          <FileText className="w-5 h-5 text-amber-500" />
          <h2 className="font-serif text-2xl">{t('stimulus.textLabel')}</h2>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-stone-700 mb-2">{t('stimulus.labelLabel')}</label>
          <input
            type="text"
            value={stimulus.stimulusLabel}
            onChange={e => stimulus.setStimulusLabel(e.target.value)}
            className="input w-full"
            placeholder={t('stimulus.labelPlaceholder')}
          />
        </div>

        <div>
          <textarea
            value={stimulus.stimulusText}
            onChange={e => stimulus.setStimulusText(e.target.value)}
            className="input w-full"
            rows={6}
            placeholder={t('stimulus.textPlaceholder')}
          />
        </div>
      </section>

      {/* Follow-up Questions */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <h2 className="font-serif text-2xl mb-2">{t('stimulus.questionsLabel')}</h2>
        {validQuestionCount > 0 && (
          <p className="text-sm text-stone-500 mb-6">
            {validQuestionCount} {validQuestionCount === 1 ? t('session.roundLabel') : t('session.roundsLabel', { n: validQuestionCount }).replace(/^\d+\s*/, '')}
          </p>
        )}

        <div className="space-y-3">
          {stimulus.questions.map((q, idx) => (
            <div key={idx} className="flex gap-2 items-start">
              <span className="text-sm text-stone-400 mt-2.5 w-6 text-right">{idx + 1}.</span>
              <input
                type="text"
                value={q}
                onChange={e => stimulus.updateQuestion(idx, e.target.value)}
                className="input flex-1"
                placeholder={t('stimulus.questionsPlaceholder')}
              />
              {stimulus.questions.length > 1 && (
                <button
                  onClick={() => stimulus.removeQuestion(idx)}
                  className="text-stone-400 hover:text-red-500 mt-2"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={stimulus.addQuestion}
          className="text-sm text-amber-600 hover:text-amber-700 mt-3"
        >
          {t('stimulus.addQuestion')}
        </button>
      </section>

      {/* Moderator Settings (collapsible) */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <button
          onClick={() => setShowModeratorSettings(!showModeratorSettings)}
          className="flex items-center gap-2 w-full text-left"
        >
          {showModeratorSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <h2 className="font-serif text-xl">{t('stimulus.moderatorSettings')}</h2>
        </button>

        {showModeratorSettings && (
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">{t('setup.briefingLabel')}</label>
              <textarea
                value={config.moderatorBriefing || ''}
                onChange={e => updateConfig('moderatorBriefing', e.target.value)}
                className="input w-full"
                rows={2}
                placeholder={t('setup.briefingPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">{t('setup.directionLabel')}</label>
              <textarea
                value={config.moderatorDirection || ''}
                onChange={e => updateConfig('moderatorDirection', e.target.value)}
                className="input w-full"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">{t('setup.avoidLabel')}</label>
              <textarea
                value={config.moderatorAvoid || ''}
                onChange={e => updateConfig('moderatorAvoid', e.target.value)}
                className="input w-full"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">{t('session.moderatorModel')}</label>
                <select
                  value={config.moderatorModel || 'claude-sonnet-4.6'}
                  onChange={e => updateConfig('moderatorModel', e.target.value)}
                  className="input w-full"
                >
                  {Object.entries(MODELS).map(([id, model]) => (
                    <option key={id} value={id}>{model.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  id="stimulus-da"
                  checked={config.devilsAdvocate || false}
                  onChange={e => updateConfig('devilsAdvocate', e.target.checked)}
                  className="w-4 h-4 accent-amber-500"
                />
                <label htmlFor="stimulus-da" className="text-sm font-medium text-stone-700">
                  {t('session.devilsAdvocate')}
                </label>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Personas */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <h2 className="font-serif text-2xl mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-amber-600" />
          {t('personas.title')}
        </h2>

        {/* Source toggle */}
        {library && (
          <div className="flex bg-stone-100 rounded-lg p-1 gap-1 mb-6 w-fit">
            <button
              onClick={() => setPanelSource('generate')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                panelSource === 'generate' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              {t('setup.panelSourceGenerate')}
            </button>
            <button
              onClick={() => setPanelSource('library')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                panelSource === 'library' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              {t('setup.panelSourceLibrary')}
            </button>
          </div>
        )}

        {panelSource === 'library' && library ? (
          /* Saved panel selection */
          <div className="space-y-4">
            {library.panels.length > 0 ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    {t('setup.selectPanel')}
                  </label>
                  <select
                    value={selectedPanelId}
                    onChange={async (e) => {
                      const panelId = e.target.value;
                      setSelectedPanelId(panelId);
                      setPanelModelAssignments({});
                      if (panelId) {
                        await library.loadPanel(panelId);
                      }
                    }}
                    className="input w-full"
                  >
                    <option value="">{t('setup.selectPanel')}...</option>
                    {library.panels.map(panel => (
                      <option key={panel.id} value={panel.id}>
                        {panel.name} ({(panel.persona_ids || []).length} {t('config.participants')})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedPanelId && library.panelPersonas.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      {t('setup.panelPreview')}
                      <span className="text-stone-400 font-normal ml-2">
                        {t('setup.panelPersonaCount', { count: library.panelPersonas.length })}
                      </span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {library.panelPersonas.map((persona, idx) => {
                        const assignedModelId = getPanelModel(persona.id, idx);
                        const assignedModel = MODELS[assignedModelId] || { name: assignedModelId, color: '#888' };
                        return (
                          <div key={persona.id} className="p-3 bg-stone-50 rounded-lg border border-stone-200 flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-stone-800 text-sm">{persona.name}</div>
                              <div className="text-xs text-stone-500 mt-0.5">
                                {persona.age && `${persona.age} ${t('persona.years')}`}
                                {persona.region && ` · ${persona.region}`}
                                {persona.employment && ` · ${persona.employment}`}
                              </div>
                            </div>
                            <select
                              value={assignedModelId}
                              onChange={(e) => setPanelModelAssignments(prev => ({ ...prev, [persona.id]: e.target.value }))}
                              className="text-xs rounded-full px-2 py-1 text-white border-0 cursor-pointer flex-shrink-0"
                              style={{ backgroundColor: assignedModel.color }}
                            >
                              {Object.entries(MODELS).map(([id, m]) => (
                                <option key={id} value={id} style={{ color: '#000', backgroundColor: '#fff' }}>{m.name}</option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-stone-500 text-sm">{t('setup.noSavedPanels')}</p>
            )}
          </div>
        ) : (
          /* Generate mode */
          <div>
            {personas.length === 0 ? (
              <div className="text-center">
                <button onClick={generatePersonas} disabled={isGeneratingPersonas} className="btn btn-primary inline-flex items-center gap-2">
                  {isGeneratingPersonas ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {t('demographics.generating')}</>
                  ) : (
                    <><Users className="w-4 h-4" /> {t('demographics.generate')}</>
                  )}
                </button>
              </div>
            ) : (
              <div>
                <h3 className="font-medium mb-3">{t('personas.title')} ({personas.length})</h3>
                <div className="flex flex-wrap gap-2">
                  {personas.map(p => (
                    <span key={p.id} className="px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-sm">
                      {p.name}, {p.age}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Templates */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-amber-600" />
            {t('stimulus.savedTemplates')}
          </h2>
          <button
            onClick={() => setShowSaveModal(true)}
            disabled={!stimulus.stimulusText.trim()}
            className="btn btn-secondary text-sm inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {t('stimulus.saveTemplate')}
          </button>
        </div>

        {templates.length === 0 ? (
          <p className="text-stone-400 text-sm">{t('stimulus.noTemplates')}</p>
        ) : (
          <div className="grid gap-2">
            {templates.map((tmpl) => (
              <div key={tmpl.id} className="flex items-center justify-between bg-stone-50 rounded-lg px-4 py-3 border border-stone-200">
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-stone-800 text-sm">{tmpl.name}</span>
                  {tmpl.config?.topic && (
                    <span className="text-stone-400 text-xs ml-2">— {tmpl.config.topic}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => loadSelectedTemplate(tmpl)}
                    className="text-xs px-3 py-1.5 bg-amber-500 text-white rounded-md hover:bg-amber-600 transition-colors"
                  >
                    {t('stimulus.loadTemplate')}
                  </button>
                  <button
                    onClick={() => deleteTemplate(tmpl.id)}
                    className="p-1.5 text-stone-300 hover:text-red-500 transition-colors"
                    title={t('stimulus.deleteTemplate')}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Save Template Modal */}
        {showSaveModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
              <h3 className="font-serif text-xl mb-4">{t('stimulus.saveTemplate')}</h3>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={t('stimulus.templateName')}
                className="input w-full mb-4"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && saveTemplate()}
              />
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowSaveModal(false)} className="btn btn-secondary">
                  {t('config.cancel')}
                </button>
                <button
                  onClick={saveTemplate}
                  disabled={!templateName.trim() || savingTemplate}
                  className="btn btn-primary inline-flex items-center gap-2"
                >
                  {savingTemplate && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('config.save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Start Button */}
      <div className="text-center">
        <button
          onClick={handleStart}
          disabled={!canStart}
          className={`btn px-8 py-3 text-lg inline-flex items-center gap-3 ${
            canStart ? 'btn-primary' : 'bg-stone-200 text-stone-400 cursor-not-allowed'
          }`}
        >
          <Play className="w-5 h-5" />
          {t('stimulus.start')}
        </button>
      </div>
    </div>
  );
}


// ─── Running Stage ──────────────────────────────────────────

export function StimulusRunning({ stimulus, config, personas, RunningStage, messagesEndRef }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      {/* Sticky stimulus reference box */}
      <div className="sticky top-0 z-10 bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-medium text-amber-800">
            {t('stimulus.referenceLabel')}{stimulus.stimulusLabel ? ` — ${stimulus.stimulusLabel}` : ''}
          </span>
        </div>
        <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">{stimulus.stimulusText}</p>
      </div>

      {/* Reuse existing RunningStage */}
      <RunningStage
        config={config}
        personas={personas}
        messages={stimulus.messages}
        currentRound={stimulus.currentRound}
        currentSpeaker={stimulus.currentSpeaker}
        streamingContent={stimulus.streamingContent}
        roundSummaries={stimulus.roundSummaries}
        messagesEndRef={messagesEndRef}
        isGeneratingReport={stimulus.isGeneratingReport}
        isPaused={false}
      />
    </div>
  );
}
